import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Camera, Search, Star, Heart, Plus, ChevronRight, List, Download, Menu, X, SlidersHorizontal, Info, Trophy, MessageSquare, Newspaper, ExternalLink, RefreshCw, Send, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API, CIGAR_PLACEHOLDER } from '../config/constants';
import BottomNav from '../components/BottomNav';
import FAB from '../components/FAB';
import CameraModal from '../components/modals/CameraModal';
import AdvancedSearchModal from '../components/modals/AdvancedSearchModal';
import CigarImage from '../components/CigarImage';

// Helper function to display cigar name without duplicating brand
const formatCigarName = (brand, name) => {
  if (!name || name.toLowerCase() === brand?.toLowerCase()) {
    return brand || 'Unknown';
  }
  return `${brand} ${name}`;
};

const formatForumTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit'
  });
};

const HomePage = () => {
  const [cigars, setCigars] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});
  const [viewMode, setViewMode] = useState('list'); // 'swipe' or 'list'
  const [listMode, setListMode] = useState('newlyAdded'); // 'top25', 'newlyAdded', 'forum', or 'news'
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [cardRating, setCardRating] = useState(0);
  const [ratingSubmitting, setRatingSubmitting] = useState(false);
  const [forumPosts, setForumPosts] = useState([]);
  const [forumLoading, setForumLoading] = useState(false);
  const [forumPostText, setForumPostText] = useState('');
  const [forumPostSubmitting, setForumPostSubmitting] = useState(false);
  const [expandedForumComments, setExpandedForumComments] = useState({});
  const [forumPostComments, setForumPostComments] = useState({});
  const [forumCommentInputs, setForumCommentInputs] = useState({});
  const [forumCommentSubmitting, setForumCommentSubmitting] = useState({});
  const [newsArticles, setNewsArticles] = useState([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsLastUpdated, setNewsLastUpdated] = useState(null);
  const [newsHasMore, setNewsHasMore] = useState(false);
  const [newsLoadingMore, setNewsLoadingMore] = useState(false);
  
  // Pull to refresh state
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshingPull, setIsRefreshingPull] = useState(false);
  const pullStartY = useRef(0);
  const listContainerRef = useRef(null);
  
  // PWA Install prompt state
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [showInstallModal, setShowInstallModal] = useState(false);
  const [installModalType, setInstallModalType] = useState('ios'); // 'ios' or 'inapp'
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Detect browser environment
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
  
  // Detect in-app browsers (Telegram, Reddit, Facebook, Instagram, Twitter, etc.)
  // These browsers don't support PWA installation - user must open in real browser first
  const isInAppBrowser = 
    // Facebook
    /FBAN|FBAV/i.test(userAgent) ||
    // Instagram
    /Instagram/i.test(userAgent) ||
    // Twitter
    /Twitter/i.test(userAgent) ||
    // Snapchat
    /Snapchat/i.test(userAgent) ||
    // Telegram (various detection methods)
    /Telegram/i.test(userAgent) ||
    // Reddit
    /Reddit/i.test(userAgent) ||
    // Line
    /Line\//i.test(userAgent) ||
    // WhatsApp
    /WhatsApp/i.test(userAgent) ||
    // Messenger
    /Messenger|FBMS/i.test(userAgent) ||
    // LinkedIn
    /LinkedIn/i.test(userAgent) ||
    // Discord
    /Discord/i.test(userAgent) ||
    // Generic WebView detection
    /WebView|wv\)/i.test(userAgent) ||
    // iOS WebView (not Safari) - this catches Telegram on iOS
    (isIOS && !navigator.standalone && !/Safari/i.test(userAgent)) ||
    // iOS apps embedding web content (like Telegram)
    (isIOS && /Mobile/i.test(userAgent) && !/Safari/i.test(userAgent));

  // Detect real Safari (not in-app browser pretending to be Safari)
  const isRealSafari = /Safari/i.test(userAgent) && !/Chrome|CriOS|FxiOS|EdgiOS/i.test(userAgent) && !isInAppBrowser;

  // Check if app is already installed (running in standalone mode)
  useEffect(() => {
    // Check if running as standalone PWA
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(isInStandaloneMode);
    
    // Check if there's already a stored prompt (event may have fired before component mounted)
    if (window.deferredPrompt) {
      setInstallPrompt(window.deferredPrompt);
    }
    
    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      // Store it globally so we don't lose it
      window.deferredPrompt = e;
      setInstallPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      window.deferredPrompt = null;
      setInstallPrompt(null);
      setIsStandalone(true);
      toast.success('App installed successfully!');
    });
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    // Check for deferred prompt (may have been stored globally)
    const prompt = installPrompt || window.deferredPrompt;
    
    if (prompt) {
      // Use the native install prompt directly (Chrome/Android)
      try {
        prompt.prompt();
        const { outcome } = await prompt.userChoice;
        if (outcome === 'accepted') {
          window.deferredPrompt = null;
          setInstallPrompt(null);
        }
      } catch (e) {
        // Prompt was already used or invalid, show manual modal
        console.log('Install prompt error:', e);
        setInstallModalType('manual');
        setShowInstallModal(true);
      }
    } else {
      // Show install modal for manual installation
      if (isInAppBrowser) {
        setInstallModalType('inapp');
      } else {
        setInstallModalType('manual');
      }
      setShowInstallModal(true);
    }
  };

  // Should show Save App button (always show unless already installed as PWA)
  const showSaveAppButton = !isStandalone;

  // Reset rating when card changes (but keep if user already rated)
  useEffect(() => {
    setCardRating(0);
  }, [currentCardIndex]);

  const handleCardRating = async (value) => {
    if (!token) {
      toast.error('Please sign in to rate');
      navigate('/auth');
      return;
    }
    
    const cigar = cigars[currentCardIndex];
    if (!cigar) return;
    
    setRatingSubmitting(true);
    try {
      await axios.post(
        `${API}/ratings`,
        { cigar_id: cigar.id, rating: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCardRating(value);
      toast.success(`Rated ${value.toFixed(1)}!`);
      // Refresh cigars to get updated ratings
      fetchCigars();
    } catch (error) {
      toast.error('Failed to submit rating');
    } finally {
      setRatingSubmitting(false);
    }
  };

  const fetchCigars = async (params = {}, retryCount = 0) => {
    try {
      // Determine sort from explicit param, then from current listMode
      const sortOrder = params._sort || (listMode === 'newlyAdded' ? 'newest' : 'rating');
      const isTop25 = params._top25 || listMode === 'top25';
      // Remove internal params before sending to API
      const { _sort, _top25, ...apiParams } = params;
      const res = await axios.get(`${API}/cigars`, {
        params: { limit: 25, sort: sortOrder, ...apiParams }
      });
      // Only sort client-side if no search query and in top25 mode
      let sortedCigars = res.data;
      if (!apiParams.search && isTop25) {
        sortedCigars = [...res.data].sort((a, b) => (b.average_rating || 0) - (a.average_rating || 0));
      }
      setCigars(sortedCigars);
      const countRes = await axios.get(`${API}/cigars/count`);
      setTotalCount(countRes.data.count);
    } catch (error) {
      // Retry up to 3 times with increasing delay (handles cold start after deployment)
      if (retryCount < 3) {
        const delay = (retryCount + 1) * 1500; // 1.5s, 3s, 4.5s
        setTimeout(() => fetchCigars(params, retryCount + 1), delay);
      } else {
        toast.error('Failed to fetch cigars. Please refresh the page.');
      }
    }
  };

  useEffect(() => {
    // Read filters from URL params
    const filters = {};
    const searchQuery = searchParams.get('search');
    const strength = searchParams.get('strength');
    const origin = searchParams.get('origin');
    const wrapper = searchParams.get('wrapper');
    const size = searchParams.get('size');
    const price = searchParams.get('price');
    const flavor = searchParams.get('flavor');

    if (searchQuery) {
      filters.search = searchQuery;
      setSearch(searchQuery);
    }
    if (strength) filters.strength = strength;
    if (origin) filters.origin = origin;
    if (wrapper) filters.wrapper = wrapper;
    if (size) filters.size = size;
    if (price) filters.price = price;
    if (flavor) filters.flavor = flavor;

    setActiveFilters(filters);
    
    // If there are search filters, always fetch cigars regardless of listMode
    // This ensures search results persist when navigating back
    if (Object.keys(filters).length > 0) {
      // Switch to a cigars view mode if we have filters
      if (listMode === 'forum' || listMode === 'news') {
        setListMode('newlyAdded');
      }
      fetchCigars(filters);
    } else if (listMode !== 'forum' && listMode !== 'news') {
      fetchCigars();
    }
  }, [searchParams]);

  // Fetch forum posts when in forum mode
  const fetchForumPosts = useCallback(async () => {
    setForumLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/forum/posts`, {
        params: { limit: 20 },
        headers
      });
      setForumPosts(res.data.posts || []);
    } catch (error) {
      console.error('Failed to fetch forum posts:', error);
    } finally {
      setForumLoading(false);
    }
  }, [token]);

  const handleForumPostSubmit = async () => {
    if (!forumPostText.trim()) return;
    if (!token) {
      navigate('/auth?reason=forum');
      return;
    }
    setForumPostSubmitting(true);
    try {
      const res = await axios.post(`${API}/forum/posts`,
        { content: forumPostText.trim(), images: [] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setForumPosts(prev => [res.data, ...prev]);
      setForumPostText('');
      toast.success('Post created!');
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setForumPostSubmitting(false);
    }
  };

  const handleForumLike = async (post) => {
    if (!token) {
      navigate('/auth?reason=forum');
      return;
    }
    try {
      if (post.liked_by_user) {
        await axios.delete(`${API}/forum/posts/${post.id}/like`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API}/forum/posts/${post.id}/like`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      setForumPosts(prev => prev.map(item => item.id === post.id ? {
        ...item,
        liked_by_user: !post.liked_by_user,
        likes_count: Math.max(0, (item.likes_count || 0) + (post.liked_by_user ? -1 : 1))
      } : item));
    } catch (error) {
      toast.error('Failed to update like');
    }
  };

  const toggleForumComments = async (postId) => {
    if (!token) {
      navigate('/auth?reason=forum');
      return;
    }
    if (expandedForumComments[postId]) {
      setExpandedForumComments(prev => ({ ...prev, [postId]: false }));
      return;
    }
    try {
      if (!forumPostComments[postId]) {
        const res = await axios.get(`${API}/forum/posts/${postId}/comments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setForumPostComments(prev => ({ ...prev, [postId]: res.data.comments || [] }));
      }
      setExpandedForumComments(prev => ({ ...prev, [postId]: true }));
    } catch (error) {
      toast.error('Failed to load comments');
    }
  };

  const submitForumComment = async (postId) => {
    const content = forumCommentInputs[postId]?.trim();
    if (!content || !token) return;
    setForumCommentSubmitting(prev => ({ ...prev, [postId]: true }));
    try {
      const res = await axios.post(`${API}/forum/posts/${postId}/comments`, { content }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForumPostComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), res.data]
      }));
      setForumPosts(prev => prev.map(post => post.id === postId ? {
        ...post, comments_count: (post.comments_count || 0) + 1
      } : post));
      setForumCommentInputs(prev => ({ ...prev, [postId]: '' }));
    } catch (error) {
      toast.error('Failed to post comment');
    } finally {
      setForumCommentSubmitting(prev => ({ ...prev, [postId]: false }));
    }
  };

  const deleteForumPost = async (postId) => {
    if (!token || !window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/forum/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForumPosts(prev => prev.filter(post => post.id !== postId));
      toast.success('Post deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  // Fetch news articles
  const fetchNews = useCallback(async (loadMore = false) => {
    if (loadMore) {
      setNewsLoadingMore(true);
    } else {
      setNewsLoading(true);
      setNewsArticles([]);
    }
    try {
      const skip = loadMore ? newsArticles.length : 0;
      const res = await axios.get(`${API}/news`, {
        params: { skip, limit: 20 }
      });
      if (loadMore) {
        setNewsArticles(prev => [...prev, ...(res.data.articles || [])]);
      } else {
        setNewsArticles(res.data.articles || []);
      }
      setNewsLastUpdated(res.data.last_updated);
      setNewsHasMore(res.data.has_more || false);
    } catch (error) {
      console.error('Failed to fetch news:', error);
    } finally {
      setNewsLoading(false);
      setNewsLoadingMore(false);
    }
  }, [newsArticles.length]);

  // Refetch when listMode changes (only if no active filters)
  useEffect(() => {
    if (listMode === 'forum') {
      fetchForumPosts();
    } else if (listMode === 'news') {
      fetchNews(false);
    } else if (Object.keys(activeFilters).length === 0) {
      // Pass explicit sort to avoid stale closure issues
      fetchCigars({
        _sort: listMode === 'newlyAdded' ? 'newest' : 'rating',
        _top25: listMode === 'top25'
      });
    }
  }, [listMode, fetchForumPosts]);

  const handleSearch = async () => {
    if (search.trim()) {
      // If we're on forum or news, switch to cigars view
      if (listMode === 'forum' || listMode === 'news') {
        setListMode('newlyAdded');
      }
      // Always fetch cigars with the search term, even if URL already has it
      const searchTerm = search.trim();
      setSearchParams({ search: searchTerm });
      setActiveFilters({ search: searchTerm });
      fetchCigars({ search: searchTerm });
    } else {
      setSearchParams({});
      setActiveFilters({});
      fetchCigars();
    }
  };

  const clearFilters = () => {
    setSearch('');
    setSearchParams({});
    setActiveFilters({});
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    const filters = {};
    searchParams.forEach((value, key) => {
      filters[key] = value;
    });
    fetchCigars(filters);
    setTimeout(() => setIsRefreshing(false), 1000);
  };

  // Pull to refresh handlers
  const handleTouchStart = (e) => {
    if (listContainerRef.current && listContainerRef.current.scrollTop === 0) {
      pullStartY.current = e.touches[0].clientY;
      setIsPulling(true);
    }
  };

  const handleTouchMove = (e) => {
    if (!isPulling || isRefreshingPull) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - pullStartY.current;
    
    if (diff > 0 && listContainerRef.current && listContainerRef.current.scrollTop === 0) {
      // Apply resistance to make it feel natural
      const resistance = 0.4;
      setPullDistance(Math.min(diff * resistance, 80));
    }
  };

  const handleTouchEnd = async () => {
    if (!isPulling) return;
    
    if (pullDistance > 50) {
      // Trigger refresh
      setIsRefreshingPull(true);
      setPullDistance(50); // Keep indicator visible during refresh
      
      try {
        if (listMode === 'forum') {
          await fetchForumPosts();
        } else if (listMode === 'news') {
          await fetchNews(false);
        } else {
          await handleRefresh();
        }
        toast.success('Refreshed!');
      } catch (error) {
        console.error('Refresh failed:', error);
      }
      
      setIsRefreshingPull(false);
    }
    
    setPullDistance(0);
    setIsPulling(false);
  };

  const handleCameraClick = () => {
    if (!token) {
      toast.error('Please sign in to use this feature');
      navigate('/auth');
      return;
    }
    setShowCamera(true);
  };

  const handleSwipe = (direction) => {
    setSwipeDirection(direction);
    setTimeout(() => {
      if (direction === 'right') {
        // Go to cigar detail on right swipe (like)
        navigate(`/cigar/${cigars[currentCardIndex].id}`);
      } else {
        // Move to next card on left swipe (skip)
        setCurrentCardIndex((prev) => (prev + 1) % cigars.length);
      }
      setSwipeDirection(null);
    }, 300);
  };

  const handleNextCard = () => {
    setSwipeDirection('peel-left');
    setTimeout(() => {
      setCurrentCardIndex((prev) => (prev + 1) % cigars.length);
      setSwipeDirection(null);
    }, 200);
  };

  const handlePrevCard = () => {
    setSwipeDirection('peel-right');
    setTimeout(() => {
      setCurrentCardIndex((prev) => (prev - 1 + cigars.length) % cigars.length);
      setSwipeDirection(null);
    }, 200);
  };

  return (
    <div className="min-h-screen bg-[#0B0B0C] pb-28">
      <div className="max-w-md mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-white/5">
          <h1 className="playfair text-2xl sm:text-3xl text-[#D97706] font-bold">Cigar Ranker</h1>
          <div className="flex items-center gap-2">
            {showSaveAppButton && (
              <button
                data-testid="save-app-button"
                onClick={handleInstallClick}
                className="flex items-center gap-1 px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-full transition-colors whitespace-nowrap"
              >
                <Download size={12} />
                Save
              </button>
            )}
            <button
              data-testid="hamburger-menu-button"
              onClick={() => setShowHamburgerMenu(true)}
              className="text-white/70 hover:text-white transition-colors p-1"
            >
              <Menu size={24} />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="p-4 flex items-center gap-2">
          <button
            data-testid="camera-button"
            onClick={handleCameraClick}
            className="bg-amber-600 hover:bg-amber-700 p-3 rounded-full transition-colors"
          >
            <Camera size={24} className="text-white" />
          </button>
          <Input
            data-testid="search-input"
            type="text"
            placeholder="Search cigars..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/30"
          />
          <button
            data-testid="search-button"
            onClick={handleSearch}
            className="bg-amber-600 hover:bg-amber-700 p-3 rounded-full transition-colors"
          >
            <Search size={24} className="text-white" />
          </button>
        </div>

        {/* Active Filters Indicator */}
        {Object.keys(activeFilters).length > 0 && (
          <div className="px-4 pb-2">
            <div className="flex items-center justify-between bg-amber-600/20 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-amber-500 text-sm font-medium">Filters:</span>
                {activeFilters.search && (
                  <span className="bg-amber-600/30 text-amber-300 text-xs px-2 py-1 rounded-full">&quot;{activeFilters.search}&quot;</span>
                )}
                {activeFilters.strength && (
                  <span className="bg-amber-600/30 text-amber-300 text-xs px-2 py-1 rounded-full">{activeFilters.strength}</span>
                )}
                {activeFilters.origin && (
                  <span className="bg-amber-600/30 text-amber-300 text-xs px-2 py-1 rounded-full">{activeFilters.origin}</span>
                )}
                {activeFilters.wrapper && (
                  <span className="bg-amber-600/30 text-amber-300 text-xs px-2 py-1 rounded-full">{activeFilters.wrapper}</span>
                )}
                {activeFilters.size && (
                  <span className="bg-amber-600/30 text-amber-300 text-xs px-2 py-1 rounded-full">{activeFilters.size}</span>
                )}
                {activeFilters.price && (
                  <span className="bg-amber-600/30 text-amber-300 text-xs px-2 py-1 rounded-full">{activeFilters.price}</span>
                )}
                {activeFilters.flavor && (
                  <span className="bg-amber-600/30 text-amber-300 text-xs px-2 py-1 rounded-full">🍫 {activeFilters.flavor}</span>
                )}
              </div>
              <button 
                onClick={clearFilters}
                className="text-amber-500 text-sm hover:text-amber-400 ml-2"
              >
                Clear
              </button>
            </div>
          </div>
        )}

        {/* Cigars List */}
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div>
              {Object.keys(activeFilters).length > 0 ? (
                <>
                  <h2 className="text-xl font-bold text-white">Search Results</h2>
                  <p className="text-sm text-white/50 pl-4 mt-1">({cigars.length} found)</p>
                </>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    data-testid="top25-button"
                    onClick={() => setListMode('top25')}
                    className={`text-base font-bold transition-colors ${
                      listMode === 'top25' ? 'text-white' : 'text-white/40 hover:text-white/70'
                    }`}
                  >
                    Top 25
                  </button>
                  <span className="text-white/30">|</span>
                  <button
                    data-testid="newly-added-button"
                    onClick={() => setListMode('newlyAdded')}
                    className={`text-base font-bold transition-colors ${
                      listMode === 'newlyAdded' ? 'text-white' : 'text-white/40 hover:text-white/70'
                    }`}
                  >
                    New Cigars
                  </button>
                  <span className="text-white/30">|</span>
                  <button
                    data-testid="forum-button"
                    onClick={() => setListMode('forum')}
                    className={`text-base font-bold transition-colors ${
                      listMode === 'forum' ? 'text-white' : 'text-white/40 hover:text-white/70'
                    }`}
                  >
                    Forum
                  </button>
                  <span className="text-white/30">|</span>
                  <button
                    data-testid="news-button"
                    onClick={() => setListMode('news')}
                    className={`text-base font-bold transition-colors ${
                      listMode === 'news' ? 'text-white' : 'text-white/40 hover:text-white/70'
                    }`}
                  >
                    News
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Pull to Refresh Indicator */}
          <div 
            className="flex justify-center overflow-hidden transition-all duration-200"
            style={{ height: pullDistance > 0 ? pullDistance : 0 }}
          >
            <div className="flex items-center gap-2 text-amber-500">
              <RefreshCw 
                size={20} 
                className={isRefreshingPull ? 'animate-spin' : ''} 
                style={{ 
                  transform: `rotate(${pullDistance * 3.6}deg)`,
                  opacity: pullDistance / 50
                }} 
              />
              <span className="text-sm" style={{ opacity: pullDistance / 50 }}>
                {isRefreshingPull ? 'Refreshing...' : pullDistance > 50 ? 'Release to refresh' : 'Pull to refresh'}
              </span>
            </div>
          </div>

          {/* Scrollable Content Area */}
          <div 
            ref={listContainerRef}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            className="flex-1 overflow-y-auto"
            style={{ touchAction: pullDistance > 0 ? 'none' : 'auto' }}
          >
          {listMode === 'forum' ? (
            /* Forum View */
            <div className="space-y-4">
              {/* Compose Box */}
              <div className="bg-[#141416] border border-white/10 rounded-xl p-3">
                <div className="flex items-start gap-3">
                  <div className="w-9 h-9 rounded-full overflow-hidden bg-white/10 flex-shrink-0 mt-0.5">
                    {user?.profile_pic ? (
                      <img src={user.profile_pic} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white/50 text-sm font-bold">
                        {user?.username?.charAt(0).toUpperCase() || '?'}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 flex items-end gap-2">
                    <textarea
                      data-testid="forum-compose-input"
                      value={forumPostText}
                      onChange={(e) => setForumPostText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleForumPostSubmit(); } }}
                      placeholder={token ? "What's on your mind?" : "Log in to post..."}
                      disabled={!token || forumPostSubmitting}
                      rows={1}
                      className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm placeholder-white/30 resize-none focus:outline-none focus:border-amber-500/50 disabled:opacity-50"
                      style={{ minHeight: '38px', maxHeight: '100px' }}
                      onInput={(e) => { e.target.style.height = '38px'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                    />
                    <button
                      data-testid="forum-compose-submit"
                      onClick={handleForumPostSubmit}
                      disabled={!forumPostText.trim() || forumPostSubmitting || !token}
                      className="p-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                    >
                      <Send size={18} />
                    </button>
                  </div>
                </div>
              </div>
              {forumLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
                </div>
              ) : forumPosts.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-white/50">No forum posts yet</p>
                </div>
              ) : (
                forumPosts.map((post) => (
                  <div
                    key={post.id}
                    data-testid={`forum-post-card-${post.id}`}
                    className="bg-[#141416] border border-white/10 rounded-xl p-4"
                  >
                    {/* Post Header */}
                    <div className="flex items-start gap-3 mb-3">
                      <div 
                        data-testid={`forum-post-profile-${post.id}`}
                        className="w-10 h-10 rounded-full overflow-hidden bg-white/10 flex-shrink-0 cursor-pointer"
                        onClick={() => navigate(`/user/${post.user_id}`)}
                      >
                        {post.user_profile_pic ? (
                          <img src={post.user_profile_pic} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-white/50 text-lg font-bold">
                            {post.username?.charAt(0).toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span 
                            data-testid={`forum-post-user-${post.id}`}
                            className="text-white font-medium hover:text-amber-500 cursor-pointer"
                            onClick={() => navigate(`/user/${post.user_id}`)}
                          >
                            {post.username}
                          </span>
                          <span className="text-white/30 text-sm">
                            {formatForumTimestamp(post.created_at)}
                          </span>
                        </div>
                        <p className="text-white/70 text-sm mt-1">{post.content}</p>
                      </div>
                    </div>
                    
                    {/* Post Images */}
                    {post.images && post.images.length > 0 && (
                      <div className={`grid gap-2 mb-3 ${post.images.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                        {post.images.slice(0, 4).map((img, idx) => (
                          <div key={idx} className="aspect-square rounded-lg overflow-hidden bg-white/5">
                            <img src={img} alt="" className="w-full h-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Cigar Link if applicable */}
                    {post.cigar_id && post.cigar_brand && (
                      <div 
                        data-testid={`forum-post-cigar-${post.id}`}
                        className="bg-white/5 rounded-lg p-3 mb-3 cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => navigate(`/cigar/${post.cigar_id}`)}
                      >
                        <div className="flex items-center gap-3">
                          {post.cigar_image && (
                            <div className="w-12 h-12 rounded-lg overflow-hidden bg-white/10">
                              <img src={post.cigar_image} alt="" className="w-full h-full object-cover" />
                            </div>
                          )}
                          <div>
                            <p className="text-white/50 text-xs">{post.cigar_brand}</p>
                            <p className="text-white font-medium text-sm">{post.cigar_name}</p>
                            {post.rating_value && (
                              <div className="flex items-center gap-1 mt-0.5">
                                <Star size={12} className="text-amber-500 fill-amber-500" />
                                <span className="text-amber-500 text-xs font-medium">{post.rating_value}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Post Actions */}
                    <div className="flex items-center gap-6 text-white/40">
                      <button 
                        data-testid={`forum-post-like-${post.id}`}
                        aria-label={post.liked_by_user ? 'Unlike post' : 'Like post'}
                        className={`flex items-center gap-1.5 transition-colors ${post.liked_by_user ? 'text-red-500' : 'hover:text-red-400'}`}
                        onClick={() => handleForumLike(post)}
                      >
                        <Heart size={18} className={post.liked_by_user ? 'fill-current' : ''} />
                        <span className="text-sm">{post.likes_count || 0}</span>
                      </button>
                      <button 
                        data-testid={`forum-post-comments-${post.id}`}
                        aria-label="View or add comments"
                        className="flex items-center gap-1.5 hover:text-white transition-colors"
                        onClick={() => toggleForumComments(post.id)}
                      >
                        <MessageSquare size={18} />
                        <span className="text-sm">{post.comments_count || 0}</span>
                      </button>
                      {user?.id === post.user_id && (
                        <button
                          data-testid={`forum-post-delete-${post.id}`}
                          aria-label="Delete post"
                          className="ml-auto text-white/40 hover:text-red-500 transition-colors"
                          onClick={() => deleteForumPost(post.id)}
                        >
                          <Trash2 size={18} />
                        </button>
                      )}
                    </div>
                    {expandedForumComments[post.id] && (
                      <div data-testid={`forum-comments-panel-${post.id}`} className="mt-4 pt-4 border-t border-white/10 space-y-3">
                        {(forumPostComments[post.id] || []).map(comment => (
                          <div key={comment.id} className="flex gap-2">
                            <div className="w-7 h-7 rounded-full bg-white/10 overflow-hidden flex-shrink-0">
                              {comment.user_profile_pic ? <img src={comment.user_profile_pic} alt="" className="w-full h-full object-cover" /> : null}
                            </div>
                            <div className="flex-1 min-w-0 bg-white/5 rounded-lg px-3 py-2">
                              <p className="text-white text-xs font-medium">{comment.username} <span className="text-white/30 font-normal">{formatForumTimestamp(comment.created_at)}</span></p>
                              <p className="text-white/70 text-sm mt-1">{comment.content}</p>
                            </div>
                          </div>
                        ))}
                        <div className="flex items-end gap-2">
                          <textarea
                            data-testid={`forum-comment-input-${post.id}`}
                            value={forumCommentInputs[post.id] || ''}
                            onChange={(e) => setForumCommentInputs(prev => ({ ...prev, [post.id]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitForumComment(post.id); } }}
                            placeholder="Write a comment..."
                            rows={1}
                            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 resize-none focus:outline-none focus:border-amber-500/50"
                          />
                          <button
                            data-testid={`forum-comment-submit-${post.id}`}
                            aria-label="Post comment"
                            disabled={!forumCommentInputs[post.id]?.trim() || forumCommentSubmitting[post.id]}
                            onClick={() => submitForumComment(post.id)}
                            className="p-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-30 transition-colors"
                          >
                            <Send size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
              
              {/* View More Link */}
              {forumPosts.length > 0 && (
                <button
                  data-testid="forum-view-all-button"
                  onClick={() => navigate('/forum')}
                  className="w-full py-3 text-center text-amber-500 hover:text-amber-400 transition-colors text-sm font-medium"
                >
                  View all posts in Forum →
                </button>
              )}
            </div>
          ) : listMode === 'news' ? (
            /* News View */
            <div className="space-y-4">
              {newsLastUpdated && (
                <p className="text-white/30 text-xs text-center">
                  Last updated: {new Date(newsLastUpdated).toLocaleDateString()} at {new Date(newsLastUpdated).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                </p>
              )}
              {newsLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mb-4"></div>
                  <p className="text-white/50 text-sm">Loading latest cigar news...</p>
                </div>
              ) : newsArticles.length === 0 ? (
                <div className="text-center py-8">
                  <Newspaper size={48} className="text-white/20 mx-auto mb-4" />
                  <p className="text-white/50">No news articles available</p>
                  <p className="text-white/30 text-sm mt-2">Check back later for updates</p>
                </div>
              ) : (
                <>
                  {newsArticles.map((article, idx) => (
                    <a
                      key={idx}
                      href={article.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block bg-[#141416] border border-white/10 rounded-xl overflow-hidden hover:border-amber-500/30 transition-all"
                    >
                      {article.image_url && (
                        <div className="w-full h-40 bg-white/5">
                          <img 
                            src={article.image_url} 
                            alt="" 
                            className="w-full h-full object-cover"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      )}
                      <div className="p-4">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="text-amber-500 text-xs font-medium uppercase tracking-wide">
                            {article.source}
                          </span>
                          <span className="text-white/30 text-xs">
                            {article.date}
                          </span>
                        </div>
                        <h3 className="text-white font-bold text-lg mb-2 line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-white/60 text-sm line-clamp-3 mb-3">
                          {article.summary}
                        </p>
                        <div className="flex items-center gap-1 text-amber-500 text-sm">
                          <span>Read more</span>
                          <ExternalLink size={14} />
                        </div>
                      </div>
                    </a>
                  ))}
                  
                  {/* Load More Button */}
                  {newsHasMore && (
                    <button
                      onClick={() => fetchNews(true)}
                      disabled={newsLoadingMore}
                      className="w-full py-4 text-center bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl text-amber-500 font-medium transition-all disabled:opacity-50"
                    >
                      {newsLoadingMore ? (
                        <span className="flex items-center justify-center gap-2">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-amber-500"></div>
                          Loading more...
                        </span>
                      ) : (
                        'Load More Articles'
                      )}
                    </button>
                  )}
                  
                  {!newsHasMore && newsArticles.length > 0 && (
                    <p className="text-center text-white/30 text-sm py-4">
                      You've reached the end of the news feed
                    </p>
                  )}
                </>
              )}
            </div>
          ) : cigars.length === 0 && Object.keys(activeFilters).length > 0 ? (
            <div className="text-center py-8">
              <p className="text-white/50 mb-4">No cigars found matching your filters</p>
              <Button onClick={() => navigate('/add-cigar')} className="bg-amber-600">
                <Plus size={18} className="mr-2" />
                Add Cigar
              </Button>
            </div>
          ) : viewMode === 'swipe' && cigars.length > 0 ? (
            /* Swipe View */
            <div className="relative">
              {/* Navigation Arrows - Outside the card */}
              <div className="absolute inset-0 flex items-center justify-between pointer-events-none z-10 px-2">
                <button
                  onClick={handlePrevCard}
                  className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-amber-500/30 hover:border-amber-500/50 transition-all pointer-events-auto shadow-lg"
                >
                  <ChevronRight size={32} className="rotate-180" />
                </button>
                <button
                  onClick={handleNextCard}
                  className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white hover:bg-amber-500/30 hover:border-amber-500/50 transition-all pointer-events-auto shadow-lg"
                >
                  <ChevronRight size={32} />
                </button>
              </div>

              {/* Card Stack */}
              <div className="relative h-[520px] flex items-center justify-center px-12">
                {/* Background cards for stack effect */}
                {cigars.length > 2 && (
                  <div 
                    className="absolute w-[75%] h-[480px] bg-[#1a1a1c] rounded-3xl border border-white/5"
                    style={{ transform: 'scale(0.9) translateY(20px)', opacity: 0.3 }}
                  />
                )}
                {cigars.length > 1 && (
                  <div 
                    className="absolute w-[82%] h-[490px] bg-[#161618] rounded-3xl border border-white/5"
                    style={{ transform: 'scale(0.95) translateY(10px)', opacity: 0.5 }}
                  />
                )}
                
                {/* Main Card */}
                <div className="relative w-full max-w-[320px]">
                  <div 
                    className={`relative w-full bg-[#141416] rounded-3xl border border-white/10 overflow-hidden shadow-2xl transition-all duration-300 ${
                      swipeDirection === 'left' ? '-translate-x-full rotate-[-20deg] opacity-0' : 
                      swipeDirection === 'right' ? 'translate-x-full rotate-[20deg] opacity-0' : 
                      swipeDirection === 'peel-left' ? 'animate-peel-left' :
                      swipeDirection === 'peel-right' ? 'animate-peel-right' : ''
                    }`}
                  >
                    {/* Clickable Image Area */}
                    <div 
                      className="cursor-pointer"
                      onClick={() => navigate(`/cigar/${cigars[currentCardIndex]?.id}`)}
                    >
                    {/* Image */}
                    <div className="h-40 bg-white/5 overflow-hidden">
                      <CigarImage 
                        src={cigars[currentCardIndex]?.image_url} 
                        alt={cigars[currentCardIndex]?.name} 
                      />
                    </div>
                    
                    {/* Content */}
                    <div className="p-4">
                      <p className="text-amber-500 text-sm font-medium">{cigars[currentCardIndex]?.brand}</p>
                      <h3 className="text-xl font-bold text-white mt-1 line-clamp-1">{cigars[currentCardIndex]?.name}</h3>
                      
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex items-center gap-1 bg-amber-500/20 px-2 py-1 rounded-full">
                          <Star className="text-amber-500 fill-amber-500" size={14} />
                          <span className="text-amber-500 font-bold text-sm">{cigars[currentCardIndex]?.average_rating?.toFixed(1)}</span>
                        </div>
                        <span className="text-white/50 text-xs">{cigars[currentCardIndex]?.strength || 'Medium'}</span>
                        <span className="text-white/50 text-xs">·</span>
                        <span className="text-white/50 text-xs">{cigars[currentCardIndex]?.origin || 'Nicaragua'}</span>
                      </div>
                      
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {(cigars[currentCardIndex]?.flavor_notes || ['Tobacco', 'Wood']).slice(0, 3).map((note, idx) => (
                          <span key={idx} className="bg-white/5 border border-white/10 px-2 py-0.5 rounded-full text-xs text-white/70">
                            {note}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Rating Section - Bottom of Card */}
                  <div className="px-4 pb-4 pt-2 border-t border-white/10">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-white/70 text-xs font-medium">Rate this cigar</p>
                      <div className="flex items-center gap-1">
                        <span className="text-xl text-white font-bold">{cardRating.toFixed(1)}</span>
                        <Star className="text-amber-500 fill-amber-500" size={16} />
                      </div>
                    </div>
                    <Slider
                      value={[cardRating]}
                      onValueChange={(value) => setCardRating(value[0])}
                      min={0}
                      max={10}
                      step={0.1}
                      className="mb-2"
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex justify-between text-xs text-white/50 mb-3">
                      <span>0</span>
                      <span>5</span>
                      <span>10</span>
                    </div>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!token) {
                          toast.info('Please sign in to rate cigars');
                          navigate('/auth');
                          return;
                        }
                        handleCardRating(cardRating);
                      }}
                      disabled={ratingSubmitting || cardRating === 0}
                      className={`w-full rounded-full py-2 text-sm transition-all ${
                        ratingSubmitting 
                          ? 'bg-amber-400' 
                          : 'bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-500 hover:to-amber-700'
                      }`}
                    >
                      {ratingSubmitting ? 'Submitting...' : 'Submit Rating'}
                    </Button>
                  </div>
                </div>
                </div>
              </div>
              
              {/* Card Counter */}
              <p className="text-center text-white/30 text-sm mt-2">
                {currentCardIndex + 1} of {cigars.length} · Tap card for details
              </p>
            </div>
          ) : (
          /* List View */
          <div className="space-y-4">
            {cigars.map((cigar) => (
              <div
                key={cigar.id}
                data-testid="cigar-card"
                onClick={() => navigate(`/cigar/${cigar.id}`)}
                className="relative overflow-hidden rounded-2xl bg-[#141416] border border-white/5 hover:border-amber-500/30 transition-all duration-300 group cursor-pointer"
              >
                <div className="flex gap-4 p-4">
                  <div className="w-20 h-20 bg-white/5 rounded-lg flex items-center justify-center overflow-hidden">
                    <CigarImage src={cigar.image_url} alt={cigar.name} />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-white/50">{cigar.brand}</p>
                    <h3 className="text-lg font-bold text-white">{formatCigarName(cigar.brand, cigar.name)}</h3>
                    <p className="text-sm text-white/70">{cigar.strength || 'Medium'} · {cigar.origin || 'Nicaragua'}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Star className="text-amber-500 fill-amber-500" size={16} />
                      <span className="text-amber-500 font-bold">{cigar.average_rating.toFixed(1)}</span>
                      <span className="text-white/50 text-sm">{cigar.price_range || '$10-12'}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
          )}
          </div>{/* End of scrollable content area */}
        </div>
      </div>

      {showCamera && <CameraModal onClose={() => setShowCamera(false)} />}
      {showAdvancedSearch && <AdvancedSearchModal onClose={() => setShowAdvancedSearch(false)} />}
      
      {/* Hamburger Menu */}
      {showHamburgerMenu && (
        <div 
          className="fixed inset-0 bg-black/80 z-50"
          onClick={(e) => e.target === e.currentTarget && setShowHamburgerMenu(false)}
        >
          <div className="absolute right-0 top-0 h-full w-72 bg-[#141416] border-l border-white/10 animate-slide-in-right">
            {/* Menu Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h2 className="text-lg font-semibold text-white">Menu</h2>
              <button
                onClick={() => setShowHamburgerMenu(false)}
                className="text-white/50 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* Menu Items */}
            <div className="p-2">
              <button
                data-testid="menu-all-cigars"
                onClick={() => {
                  setShowHamburgerMenu(false);
                  navigate('/browse-brands');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <List size={20} className="text-amber-500" />
                <span>All Cigars</span>
              </button>
              
              <button
                data-testid="menu-leaderboards"
                onClick={() => {
                  setShowHamburgerMenu(false);
                  navigate('/leaderboards');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <Trophy size={20} className="text-amber-500" />
                <span>Leaderboards</span>
              </button>
              
              <button
                data-testid="menu-advanced-search"
                onClick={() => {
                  setShowHamburgerMenu(false);
                  setShowAdvancedSearch(true);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <SlidersHorizontal size={20} className="text-amber-500" />
                <span>Advanced Search</span>
              </button>
              
              <button
                data-testid="menu-about"
                onClick={() => {
                  setShowHamburgerMenu(false);
                  navigate('/about');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <Info size={20} className="text-amber-500" />
                <span>About</span>
              </button>
              
              <button
                data-testid="menu-feedback"
                onClick={() => {
                  setShowHamburgerMenu(false);
                  setShowFeedbackModal(true);
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-white/80 hover:text-white hover:bg-white/5 rounded-lg transition-colors"
              >
                <MessageSquare size={20} className="text-amber-500" />
                <span>Feedback</span>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Install Instructions Modal */}
      {showInstallModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={(e) => e.target === e.currentTarget && setShowInstallModal(false)}
        >
          <div className="bg-[#141416] w-full max-w-sm rounded-2xl p-6 animate-slide-up">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-amber-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Download className="text-amber-500" size={32} />
              </div>
              <h2 className="text-xl text-white font-semibold mb-2">Add to Home Screen</h2>
              <p className="text-white/60 text-sm">
                {installModalType === 'inapp' 
                  ? "Open in your browser to save this app"
                  : "Save Cigar Ranker to your home screen"}
              </p>
            </div>
            
            {installModalType === 'inapp' ? (
              /* In-App Browser - need to open in real browser */
              <div className="space-y-3 mb-6">
                <p className="text-white/70 text-center text-sm">
                  You're viewing this in an app browser. To save to your home screen:
                </p>
                <div className="bg-white/5 rounded-xl p-4">
                  <p className="text-white text-sm">
                    1. Tap the <strong>menu (⋮)</strong> or <strong>(...)</strong>
                  </p>
                  <p className="text-white text-sm mt-2">
                    2. Select <strong>"Open in Browser"</strong>
                  </p>
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(window.location.href);
                    toast.success('Link copied! Open your browser and paste it');
                  }}
                  className="w-full py-3 bg-amber-600 hover:bg-amber-700 rounded-xl text-white font-medium transition-colors"
                >
                  Copy Link
                </button>
              </div>
            ) : (
              /* Manual installation instructions */
              <div className="space-y-4 mb-6">
                {isIOS || isRealSafari ? (
                  /* iOS/Safari - Share button method */
                  <>
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <p className="text-white text-sm mb-3">
                        Tap the <strong>Share</strong> button in Safari
                      </p>
                      <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center mx-auto">
                        <svg className="w-6 h-6 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                      </div>
                      <p className="text-white/60 text-xs mt-3">
                        Then tap <strong>"Add to Home Screen"</strong>
                      </p>
                    </div>
                  </>
                ) : (
                  /* Chrome/Other browsers */
                  <>
                    <div className="bg-white/5 rounded-xl p-4 text-center">
                      <p className="text-white text-sm mb-3">
                        Tap the <strong>menu (⋮)</strong> in your browser
                      </p>
                      <p className="text-white/60 text-xs">
                        Then tap <strong>"Add to Home Screen"</strong> or <strong>"Install App"</strong>
                      </p>
                    </div>
                  </>
                )}
              </div>
            )}
            
            <Button
              onClick={() => setShowInstallModal(false)}
              className="w-full bg-white/10 hover:bg-white/15 text-white"
            >
              Close
            </Button>
          </div>
        </div>
      )}
      
      {/* Feedback Modal */}
      {showFeedbackModal && (
        <FeedbackModal onClose={() => setShowFeedbackModal(false)} />
      )}
      
      <BottomNav />
      <FAB />
    </div>
  );
};

// Feedback Modal Component
const FeedbackModal = ({ onClose }) => {
  const [feedbackType, setFeedbackType] = useState('general');
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const feedbackTypes = [
    { value: 'general', label: 'General Feedback' },
    { value: 'bug', label: 'Report a Bug' },
    { value: 'feature', label: 'Feature Request' },
    { value: 'cigar', label: 'Cigar Data Issue' },
    { value: 'other', label: 'Other' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      toast.error('Please enter your feedback');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/feedback`, {
        type: feedbackType,
        message: message.trim(),
        email: email.trim() || null
      });
      setSubmitted(true);
      toast.success('Thank you for your feedback!');
    } catch (error) {
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div 
        className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <div className="bg-[#141416] w-full max-w-md rounded-2xl p-6 text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <MessageSquare className="text-green-500" size={32} />
          </div>
          <h2 className="text-xl text-white font-semibold mb-2">Thank You!</h2>
          <p className="text-white/60 mb-6">
            Your feedback has been received. We appreciate you taking the time to help us improve Cigar Ranker.
          </p>
          <Button
            onClick={onClose}
            className="w-full bg-amber-600 hover:bg-amber-700"
          >
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black/80 z-50 flex items-end sm:items-center justify-center"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#141416] w-full max-w-md rounded-t-2xl sm:rounded-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#141416] flex items-center justify-between p-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">Send Feedback</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="text-white/70 text-sm mb-2 block">What type of feedback?</label>
            <div className="grid grid-cols-2 gap-2">
              {feedbackTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => setFeedbackType(type.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    feedbackType === type.value
                      ? 'bg-amber-600 text-white'
                      : 'bg-white/10 text-white/60 hover:bg-white/20'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1 block">Your Feedback *</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={
                feedbackType === 'bug' 
                  ? "Please describe what happened and what you expected..."
                  : feedbackType === 'feature'
                  ? "What feature would you like to see?"
                  : feedbackType === 'cigar'
                  ? "Which cigar has incorrect information?"
                  : "Share your thoughts with us..."
              }
              rows={4}
              className="w-full px-3 py-2 bg-white/5 border border-white/10 text-white rounded-lg placeholder:text-white/30 resize-none focus:outline-none focus:border-amber-500"
              required
            />
          </div>

          <div>
            <label className="text-white/70 text-sm mb-1 block">Email (optional)</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
            />
            <p className="text-white/40 text-xs mt-1">
              Include your email if you'd like us to follow up
            </p>
          </div>

          <Button
            type="submit"
            disabled={submitting || !message.trim()}
            className="w-full bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
          >
            {submitting ? 'Sending...' : 'Submit Feedback'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default HomePage;
